/**
 * Inventory helpers — single place for PDP stock / CTA / ATC inventory decisions.
 *
 * Low-level: SCAPI `product.inventory` → {@link getInventory} / ATS helpers.
 * Linked finished-good model: cover → `inventoryProductId` / `linkedInventory`,
 * replacement carousel ids, finished-good PDP OOS policy — see helpers below.
 * Policy toggles: `@amblique/king-living/app/constants/inventory` (`INVENTORY_POLICY`).
 *
 * IM-004: When `getProduct(s)` is called with `inventoryIds`, SCAPI may return multiple inventory
 * records; use {@link pickInventoryRecordForListIds} via the optional third argument to `getInventory`.
 */

import {INVENTORY_POLICY, REPLACEMENT_PRODUCT_LINK_TYPE} from '@amblique/king-living/app/constants/inventory';
import {
  getAllCoverVariants,
  getSelectedVariantProduct,
} from '@amblique/king-living/app/utils/customize-selections';
import {
  hasMasterCoverVariants,
  hasVisibleSimpleProductOptions,
  isFinishedGood,
} from '@amblique/king-living/app/utils/product-utils';
import {
  getStockLevelFromAtsFields,
  isPerpetualVariant,
  PERPETUAL_ATS_THRESHOLD,
} from './cover-stock-display';

/**
 * DE-313 inventory behaviour family. Detected from product shape — not from
 * marketing name, `c_isGuidedPDP` (UI only: Sets vs swatches), or “any cover
 * has `inventoryProductId`”.
 *
 * @see getInventoryProductFamily
 */
export const INVENTORY_PRODUCT_FAMILY = Object.freeze({
  A: 'A',
  B: 'B',
  FG: 'FG',
  NONE: 'NONE',
});

/**
 * Common SFCC perpetual sentinel ATS (below {@link PERPETUAL_ATS_THRESHOLD}).
 * Finished goods must never treat this as real on-hand stock.
 */
const SFCC_PERPETUAL_SENTINEL_ATS = 999999;
/**
 * @param {Object|null|undefined} source - Product or variant
 * @returns {Array|null}
 */
function listInventoryRecords(source) {
  if (!source || typeof source !== 'object') {
    return null;
  }

  const arr = source.inventories ?? source.inventoryRecords;

  return Array.isArray(arr) && arr.length > 0 ? arr : null;
}

/**
 * @param {Object} record - Single SCAPI inventory record
 * @returns {Object}
 */
function normalizeInventoryRecordShape(record) {
  if (!record || typeof record !== 'object') {
    return record;
  }

  const ats = record.ats ?? record.stockLevel ?? record.availableToSell ?? 0;

  return {
    ...record,
    ats,
    stockLevel: record.stockLevel ?? ats,
  };
}

/**
 * Pick the inventory record matching warehouse list ids when multiple are returned (IM-004).
 *
 * @param {Object|null|undefined} inventorySource - Product or variant carrying inventory fields
 * @param {string|undefined|null} inventoryIdsCsv - Same formatted string passed to SCAPI `inventoryIds`
 * @returns {Object|null}
 */
export function pickInventoryRecordForListIds(inventorySource, inventoryIdsCsv) {
  const ids =
    inventoryIdsCsv ?
      inventoryIdsCsv.split(',').map((s) => s.trim()).filter(Boolean) :
      [];

  const records = listInventoryRecords(inventorySource);

  if (!records || ids.length === 0) {
    return null;
  }

  for (const id of ids) {
    const match = records.find((r) => r && (
      r.inventoryListId === id ||
      r.id === id ||
      r.inventoryId === id ||
      String(r.inventoryListId) === String(id)
    ));

    if (match) {
      return normalizeInventoryRecordShape(match);
    }
  }

  return null;
}

/**
 * @enum {string}
 */
export const INVENTORY_STATUS = Object.freeze({
  IN_STOCK: 'in-stock',
  LOW_STOCK: 'low-stock',
  OUT_OF_STOCK: 'out-of-stock',
  UNKNOWN: 'unknown',
});

/**
 * Inventory checks from `product.inventory` (KINDW-1127).
 *
 * Status is determined by `orderable` + `ats` only:
 * - `!orderable || ats <= 0` → `OUT_OF_STOCK`
 * - `orderable && ats >= 1 && ats <= 4` → `LOW_STOCK`
 * - `orderable && ats > 4` → `IN_STOCK`
 *
 * @param {Object|null|undefined} product
 * @param {Object|null|undefined} variant - Optional variant to check instead of product
 * @param {string|undefined|null} [inventoryIdsFromCookie] - Formatted `inventoryIds` sent to SCAPI (IM-004)
 * @returns {{
 *   status: string,
 *   canAddToCart: boolean,
 *   showNotifyMe: boolean
 * }}
 */
export const getInventory = (product, variant, inventoryIdsFromCookie) => {
  const empty = {
    status: INVENTORY_STATUS.UNKNOWN,
    canAddToCart: false,
    showNotifyMe: false,
  };

  // Supports two variant shapes:
  // 1. Standard SCAPI shape: variant.inventory sub-object
  // 2. c_allVariantsData shape: direct ats/orderable top-level fields
  const variantHasDirectFields = variant && (variant.ats !== undefined || variant.orderable !== undefined);
  const inventorySource = (variant?.inventory || variantHasDirectFields) ? variant : product;

  const picked = pickInventoryRecordForListIds(inventorySource, inventoryIdsFromCookie);
  let inventory = picked ?? inventorySource?.inventory;

  // c_allVariantsData variant: normalize direct fields into an inventory-shaped object
  if (!inventory && variantHasDirectFields) {
    inventory = {ats: variant.ats, stockLevel: variant.stockLevel ?? variant.ats, orderable: variant.orderable};
  }

  if (!inventory) {
    return empty;
  }

  const orderable = inventory.orderable === true;

  if (!orderable) {
    return {status: INVENTORY_STATUS.OUT_OF_STOCK, canAddToCart: false, showNotifyMe: true};
  }

  const inventoryEntity = variant ?? product;

  // Made-to-order / perpetual variants (incl. c_allVariantsData with ats:0 + perpetual:true).
  if (isPerpetualVariant(inventoryEntity) || isMtoOrderableProduct(inventoryEntity)) {
    return {status: INVENTORY_STATUS.IN_STOCK, canAddToCart: true, showNotifyMe: false};
  }

  // atsRaw is null/undefined when the variant has orderable:true but no ATS field.
  // In that case, treat as IN_STOCK (do not fall back to 0).
  const atsRaw = inventory.ats ?? inventory.stockLevel;

  if (atsRaw != null && atsRaw <= 0) {
    return {status: INVENTORY_STATUS.OUT_OF_STOCK, canAddToCart: false, showNotifyMe: true};
  }

  if (atsRaw != null && atsRaw <= 4) {
    return {status: INVENTORY_STATUS.LOW_STOCK, canAddToCart: true, showNotifyMe: false};
  }

  return {status: INVENTORY_STATUS.IN_STOCK, canAddToCart: true, showNotifyMe: false};
};

/**
 * Resolve the raw ATS / stockLevel value for a product without coupling to the
 * `IN_STOCK / LOW_STOCK / OUT_OF_STOCK` semantics of {@link getInventory}.
 *
 * Used by callers that need to look at the bare ATS number (e.g. the Payment
 * Amount visibility logic, which treats the SFCC sentinel `999999` as
 * "orderable but no on-hand inventory" rather than "in stock"). When multiple
 * inventory records are returned (IM-004) we pick the warehouse record that
 * matches `inventoryIdsFromCookie`; otherwise we fall back to the default
 * `product.inventory`.
 *
 * Returns `null` when no inventory record is present so callers can
 * distinguish "no data" from `ats === 0`.
 *
 * @param {Object|null|undefined} product - SCAPI product or variant
 * @param {string|undefined|null} [inventoryIdsFromCookie] - Formatted `inventoryIds` sent to SCAPI (IM-004)
 * @returns {number|null}
 */
export const getProductAts = (product, inventoryIdsFromCookie) => {
  if (!product) return null;

  const picked = pickInventoryRecordForListIds(product, inventoryIdsFromCookie);
  const inventory = picked ?? product?.inventory;

  if (!inventory) return null;

  const ats = inventory.ats ?? inventory.stockLevel;

  return typeof ats === 'number' ? ats : null;
};

/**
 * Resolve the raw ATS for a single product/variant entity, tolerating BOTH
 * inventory shapes {@link getInventory} understands:
 *   1. the SCAPI `.inventory` (or IM-004 `.inventories`) record, and
 *   2. the `c_allVariantsData` direct-fields shape (top-level `ats` / `orderable`).
 *
 * Returns the bare ATS number (or `null` when unknown) so callers can apply
 * their own thresholds — e.g. the deposit "in-stock" rule that treats the
 * `999999` sentinel as NOT in stock. Operates on the passed entity ONLY (no
 * master fallback): deposit eligibility checks the exact entity the shopper has
 * active (the selected variant for a master-variant product, otherwise the
 * product), mirroring the checkout Payment Amount rule which evaluates the
 * basket line's own product.
 *
 * @param {Object|null|undefined} entity - SCAPI product/variant or `c_allVariantsData` entry
 * @param {string|undefined|null} [inventoryIdsFromCookie] - Formatted `inventoryIds` sent to SCAPI (IM-004)
 * @returns {number|null}
 */
export const resolveEntityAts = (entity, inventoryIdsFromCookie) => {
  if (!entity) return null;

  const hasDirectFields = entity.ats !== undefined || entity.orderable !== undefined;

  if (hasDirectFields && !entity.inventory) {
    const directAts = entity.ats ?? entity.stockLevel;

    return typeof directAts === 'number' ? directAts : null;
  }

  return getProductAts(entity, inventoryIdsFromCookie);
};

/**
 * MTO (made-to-order): orderable with zero ATS — still addable; not Notify Me OOS.
 *
 * @param {Object|null|undefined} productOrVariant - SCAPI product or `c_allVariantsData` entry
 * @returns {boolean}
 */
export function isMtoOrderableProduct(productOrVariant) {
  if (!productOrVariant) return false;

  if (isPerpetualVariant(productOrVariant)) {
    return true;
  }

  const inv = productOrVariant.inventory ?? {};
  const ats = productOrVariant.ats ?? inv.ats;
  const orderable = productOrVariant.orderable ?? inv.orderable;

  return orderable === true && ats === 0;
}

/**
 * Pick orderable / ATS / perpetual from a product, variant, or linkedInventory-shaped subject.
 *
 * @param {Object|null|undefined} entity
 * @param {string|null|undefined} [inventoryIdsFromCookie]
 * @returns {{ats: number|null, orderable: boolean, perpetual: boolean, inventory: Object|null}}
 */
function pickEntityInventoryFields(entity, inventoryIdsFromCookie) {
  if (!entity) {
    return {ats: null, orderable: false, perpetual: false, inventory: null};
  }

  const hasDirectFields = entity.ats !== undefined || entity.orderable !== undefined;
  const picked = pickInventoryRecordForListIds(entity, inventoryIdsFromCookie);
  let inventory = picked ?? entity.inventory ?? null;

  if (!inventory && hasDirectFields) {
    inventory = {
      ats: entity.ats,
      stockLevel: entity.stockLevel ?? entity.ats,
      orderable: entity.orderable,
      perpetual: entity.perpetual === true,
    };
  }

  if (!inventory) {
    return {ats: null, orderable: false, perpetual: false, inventory: null};
  }

  const atsRaw = inventory.ats ?? inventory.stockLevel;
  const ats = typeof atsRaw === 'number' ? atsRaw : null;
  const orderable = inventory.orderable === true;
  const perpetual =
    inventory.perpetual === true ||
    entity.perpetual === true ||
    entity.inventory?.perpetual === true ||
    isPerpetualVariant(entity) ||
    (ats != null && (ats >= PERPETUAL_ATS_THRESHOLD || ats === SFCC_PERPETUAL_SENTINEL_ATS));

  return {ats, orderable, perpetual, inventory};
}

/**
 * Finished-good inventory only — tracked on-hand ATS, never MTO / perpetual shortcuts.
 *
 * Misconfigured FG with `perpetual: true` (or sentinel ATS like 999999) is treated as
 * **ATS 0 / out of stock**, so cover ATC falls through to Notify Me / silent MTO and the
 * FG PDP is not purchasable via the generic `getInventory` MTO path.
 *
 * @param {Object|null|undefined} productOrSubject - FG SCAPI product or linkedInventory shape
 * @param {string|null|undefined} [inventoryIdsFromCookie]
 * @returns {{
 *   status: string,
 *   canAddToCart: boolean,
 *   showNotifyMe: boolean,
 *   ats: number|null,
 * }}
 */
export function getFinishedGoodInventory(productOrSubject, inventoryIdsFromCookie) {
  const empty = {
    status: INVENTORY_STATUS.UNKNOWN,
    canAddToCart: false,
    showNotifyMe: false,
    ats: null,
  };

  if (!productOrSubject) {
    return empty;
  }

  const {ats, orderable, perpetual} = pickEntityInventoryFields(
    productOrSubject,
    inventoryIdsFromCookie
  );

  // Perpetual / sentinel / MTO-shaped FG → force OOS (as if ATS were 0).
  if (perpetual || isMtoOrderableProduct(productOrSubject)) {
    return {
      status: INVENTORY_STATUS.OUT_OF_STOCK,
      canAddToCart: false,
      showNotifyMe: true,
      ats: 0,
    };
  }

  if (!orderable) {
    return {
      status: INVENTORY_STATUS.OUT_OF_STOCK,
      canAddToCart: false,
      showNotifyMe: true,
      ats: ats ?? 0,
    };
  }

  // Require a real tracked ATS > 0 (missing ATS is not "in stock" for FGs).
  if (ats == null || ats <= 0) {
    return {
      status: INVENTORY_STATUS.OUT_OF_STOCK,
      canAddToCart: false,
      showNotifyMe: true,
      ats: ats ?? 0,
    };
  }

  if (ats <= 4) {
    return {
      status: INVENTORY_STATUS.LOW_STOCK,
      canAddToCart: true,
      showNotifyMe: false,
      ats,
    };
  }

  return {
    status: INVENTORY_STATUS.IN_STOCK,
    canAddToCart: true,
    showNotifyMe: false,
    ats,
  };
}

/**
 * True when the SKU should show Notify Me / block customize ATC (excludes MTO).
 *
 * @param {Object|null|undefined} product
 * @param {Object|null|undefined} variant
 * @param {string|undefined|null} [inventoryIdsFromCookie]
 * @returns {boolean}
 */
export function shouldShowNotifyMeForProduct(product, variant, inventoryIdsFromCookie) {
  if (variant) {
    if (isMtoOrderableProduct(variant)) {
      return false;
    }

    const variantInventory = getInventory(null, variant, inventoryIdsFromCookie);

    if (variantInventory.showNotifyMe) {
      return true;
    }
  }

  if (isMtoOrderableProduct(product)) {
    return false;
  }

  return getInventory(product, variant, inventoryIdsFromCookie).showNotifyMe;
}

// ---------------------------------------------------------------------------
// Linked finished-good / cover inventory (canonical PDP decision helpers)
// Prefer these over ad-hoc ATS checks in components.
// ---------------------------------------------------------------------------

/**
 * @param {Object|null|undefined} variant - c_allVariantsData entry or SCAPI variant
 * @returns {string|null}
 */
export function getInventoryProductIdFromVariant(variant) {
  if (!variant) return null;

  const raw =
    variant.inventoryProductId ??
    variant.c_inventoryProductId ??
    variant.linkedInventory?.id ??
    null;

  if (raw == null || raw === '') return null;

  return String(raw);
}

/**
 * Unique finished-good ids linked from cover swatch entries (`inventoryProductId`).
 *
 * @param {Array<Object|null|undefined>|null|undefined} variants
 * @returns {string[]}
 */
export function collectCoverLinkedFinishedGoodIds(variants) {
  return uniqueProductIds(
    (variants || []).map((entry) => getInventoryProductIdFromVariant(entry))
  );
}

/**
 * Build an SFRA-shaped `linkedInventory` snapshot from a warehouse-scoped SCAPI FG.
 *
 * @param {Object|null|undefined} fgProduct
 * @param {string|null|undefined} [inventoryIdsFromCookie]
 * @returns {Object|null}
 */
export function buildLinkedInventoryFromFinishedGood(fgProduct, inventoryIdsFromCookie) {
  if (!fgProduct) return null;

  const inv = getFinishedGoodInventory(fgProduct, inventoryIdsFromCookie);
  const isBackInStock = resolveIsBackInStock(fgProduct, null);
  const isInStock =
    inv.canAddToCart === true &&
    typeof inv.ats === 'number' &&
    inv.ats > 0;

  return {
    id: String(fgProduct.id || fgProduct.productId || ''),
    ats: inv.ats,
    orderable: inv.canAddToCart === true,
    perpetual: false,
    isBackInStock,
    hideOosBadge: !isInStock && !isBackInStock,
  };
}

/**
 * When PDP warehouse `inventoryIds` are active, replace the SFRA site-default
 * `linkedInventory` snapshot with warehouse-scoped FG records. While a linked FG
 * is still loading, clear the default snapshot so swatches/preselect do not use
 * another warehouse's ATS.
 *
 * When `inventoryIdsFromCookie` is unset, returns `variants` unchanged.
 *
 * @param {Array<Object>|null|undefined} variants
 * @param {Record<string, Object>|null|undefined} productsById - warehouse getProducts map
 * @param {string|null|undefined} inventoryIdsFromCookie
 * @param {{hydrationComplete?: boolean}} [options]
 * @returns {Array<Object>}
 */
export function mergeCoverVariantsWithWarehouseInventory(
  variants,
  productsById,
  inventoryIdsFromCookie,
  {hydrationComplete = true} = {}
) {
  if (!inventoryIdsFromCookie || !Array.isArray(variants)) {
    return variants || [];
  }

  const map = productsById || {};

  return variants.map((variant) => {
    const fgId = getInventoryProductIdFromVariant(variant);

    if (!fgId) return variant;

    const liveFg = map[fgId] ?? null;

    if (!liveFg) {
      if (!hydrationComplete) {
        return {
          ...variant,
          linkedInventory: null,
          linkedInventoryWarehousePending: true,
        };
      }

      // Hydration finished but FG missing from SCAPI — silent-MTO-safe placeholder.
      return {
        ...variant,
        linkedInventory: {
          id: fgId,
          ats: 0,
          orderable: false,
          perpetual: false,
          isBackInStock: false,
          hideOosBadge: true,
        },
        linkedInventoryWarehousePending: false,
      };
    }

    return {
      ...variant,
      linkedInventory: buildLinkedInventoryFromFinishedGood(liveFg, inventoryIdsFromCookie),
      linkedInventoryWarehousePending: false,
    };
  });
}

/**
 * Classify the DE-313 inventory family from product data.
 *
 * 1. Finished good (`isFinishedGood`) → FG
 * 2. At least one **visible** OGU option → Family B (Jasper / Myco)
 * 3. Master with covers and no visible options → Family A (Freeform / 1977)
 *
 * `c_isGuidedPDP` is not consulted (Sets vs swatches UI only).
 * Cover `inventoryProductId` is not consulted (swatch / ATC pointer after family).
 *
 * @param {Object|null|undefined} product
 * @returns {'A'|'B'|'FG'|'NONE'}
 */
export function getInventoryProductFamily(product) {
  if (!product) return INVENTORY_PRODUCT_FAMILY.NONE;
  if (isFinishedGood(product)) return INVENTORY_PRODUCT_FAMILY.FG;
  if (hasVisibleSimpleProductOptions(product)) return INVENTORY_PRODUCT_FAMILY.B;
  if (hasMasterCoverVariants(product)) return INVENTORY_PRODUCT_FAMILY.A;

  return INVENTORY_PRODUCT_FAMILY.NONE;
}

/**
 * Family A: no visible options. Cover `inventoryProductId` then drives swatch
 * stock and in-stock ATC target. Unlinked cover → OOS + Notify Me.
 *
 * @param {Object|null|undefined} product
 * @returns {boolean}
 */
export function isInventoryFamilyA(product) {
  return getInventoryProductFamily(product) === INVENTORY_PRODUCT_FAMILY.A;
}

/**
 * Family B: at least one visible option. Customize stays MTO. Leftover cover
 * `inventoryProductId` must not flip CTA to Notify Me or ATC-the-FG.
 *
 * @param {Object|null|undefined} product
 * @returns {boolean}
 */
export function isInventoryFamilyB(product) {
  return getInventoryProductFamily(product) === INVENTORY_PRODUCT_FAMILY.B;
}

/**
 * True when at least one cover has `inventoryProductId` / `linkedInventory`.
 *
 * Not a family detector. After {@link isInventoryFamilyA}, use this (or the
 * selected cover’s id) to hydrate linked FGs for swatch labels / ATC.
 *
 * @param {Object|null|undefined} product - Master product
 * @returns {boolean}
 */
export function masterUsesInventoryProductModel(product) {
  const variants = getAllCoverVariants(product);

  return variants.some((entry) => Boolean(getInventoryProductIdFromVariant(entry)));
}

/**
 * Shop API `productLinks` includes both directions: outgoing (this product is
 * the source) and incoming (this product is the target). Want It Sooner only
 * follows master → FG, i.e. links this product owns.
 *
 * @param {Object|null|undefined} product
 * @returns {string[]} outgoing replacement target product ids
 */
export function getReplacementFinishedGoodIds(product) {
  const links = product?.productLinks;

  if (!Array.isArray(links) || links.length === 0) return [];

  const selfId = String(product?.id || product?.productId || '');

  return links
    .filter((link) => {
      const type = String(link?.type || '').toLowerCase();

      if (type !== REPLACEMENT_PRODUCT_LINK_TYPE) return false;

      const sourceId = link.sourceProductId ? String(link.sourceProductId) : '';
      const targetId = String(link.targetProductId || link.productId || link.id || '');

      if (!targetId) return false;
      // Incoming link: another product named this one as its replacement.
      if (selfId && targetId === selfId) return false;
      if (selfId && sourceId && sourceId !== selfId) return false;

      return true;
    })
    .map((link) => String(link.targetProductId || link.productId || link.id));
}

/**
 * Deduplicate product ids, preserving first-seen order.
 *
 * @param {Array<string|number|null|undefined>} ids
 * @returns {string[]}
 */
export function uniqueProductIds(ids) {
  const seen = new Set();
  const out = [];

  (ids || []).forEach((id) => {
    if (id === null || typeof id === 'undefined' || id === '') return;

    const key = String(id);

    if (seen.has(key)) return;

    seen.add(key);
    out.push(key);
  });

  return out;
}

/**
 * Replacement FG ids from a list of hydrated masters (and any product that
 * already carries `productLinks` of type replacement).
 *
 * @param {Array<Object|null|undefined>} products
 * @returns {string[]}
 */
export function collectReplacementFinishedGoodIdsFromProducts(products) {
  const ids = [];

  (products || []).forEach((product) => {
    ids.push(...getReplacementFinishedGoodIds(product));
  });

  return uniqueProductIds(ids);
}

/**
 * Variant hits in category search do not carry the master's replacement links.
 * Return master ids that still need a getProducts fetch.
 *
 * @param {Array<Object|null|undefined>} products
 * @param {Array<string>} alreadyFetchedIds
 * @returns {string[]}
 */
export function collectUnresolvedMasterIds(products, alreadyFetchedIds = []) {
  const fetched = new Set((alreadyFetchedIds || []).map(String));
  const missing = [];

  (products || []).forEach((product) => {
    if (getReplacementFinishedGoodIds(product).length > 0) return;

    const masterId = product?.master?.masterId || product?.master?.id;

    if (!masterId) return;

    const key = String(masterId);
    const selfId = String(product?.id || product?.productId || '');

    if (fetched.has(key) || key === selfId) return;

    missing.push(key);
  });

  return uniqueProductIds(missing);
}

/**
 * Merge current PDP product into the hydrated category list when search omitted it.
 *
 * @param {Array<Object>} categoryProducts
 * @param {Object|null|undefined} currentProduct
 * @returns {Object[]}
 */
export function mergeCurrentProductIntoMasters(categoryProducts, currentProduct) {
  const list = Array.isArray(categoryProducts) ? [...categoryProducts] : [];
  const currentId = currentProduct?.id || currentProduct?.productId;

  if (!currentId) return list;

  const key = String(currentId);
  const exists = list.some((product) => String(product?.id || product?.productId) === key);

  if (!exists) list.push(currentProduct);

  return list;
}

/**
 * Want It Sooner tiles: in-stock FGs, or (future) masters that have at least one
 * in-stock replacement FG. Never include the product already on screen.
 *
 * @param {Object} options
 * @param {'finished-good'|'master'} options.tileSubject
 * @param {Array<Object>} options.masters
 * @param {Array<Object>} options.instockFinishedGoods
 * @param {string|null|undefined} options.currentProductId
 * @returns {Array<Object>}
 */
export function selectWantItSoonerTiles({
  tileSubject,
  masters,
  instockFinishedGoods,
  currentProductId,
}) {
  const currentId = currentProductId ? String(currentProductId) : '';
  const excludeCurrent = (product) => {
    const id = String(product?.id || product?.productId || '');

    return id && id !== currentId;
  };

  if (tileSubject === 'master') {
    const instockFgIds = new Set(
      (instockFinishedGoods || []).map((product) => String(product?.id || product?.productId || ''))
    );

    return (masters || []).filter((master) => {
      if (!excludeCurrent(master)) return false;

      return getReplacementFinishedGoodIds(master).some((fgId) => instockFgIds.has(String(fgId)));
    });
  }

  return (instockFinishedGoods || []).filter(excludeCurrent);
}

/**
 * True when master has outgoing `replacement` product links (Want It Sooner feed).
 *
 * Not a family detector. Family B (visible options) hides swatch stock via
 * {@link shouldHideCoverStockLabels}; Family A may also carry replacement links.
 *
 * @param {Object|null|undefined} product
 * @returns {boolean}
 */
export function masterUsesReplacementCarousel(product) {
  return getReplacementFinishedGoodIds(product).length > 0;
}

/**
 * Hide cover swatch In stock / Low stock / Sold out labels (show price rule instead).
 *
 * Family B (visible options): never show real stock on covers (AC5), even when
 * leftover `inventoryProductId`s exist. Family A: keep swatch stock from the
 * selected cover’s linked FG. `replacement` links are Want It Sooner only.
 *
 * @param {Object|null|undefined} product
 * @returns {boolean}
 */
export function shouldHideCoverStockLabels(product) {
  return isInventoryFamilyB(product);
}

/**
 * Build a variant-shaped inventory subject from SFRA `linkedInventory` snapshot
 * so {@link getInventory} / stock classifiers can consume it.
 *
 * @param {Object} linkedInventory
 * @returns {Object}
 */
export function linkedInventoryAsVariantShape(linkedInventory) {
  return {
    ats: linkedInventory.ats,
    orderable: linkedInventory.orderable,
    perpetual: linkedInventory.perpetual === true,
    inventory: {
      ats: linkedInventory.ats,
      orderable: linkedInventory.orderable,
      perpetual: linkedInventory.perpetual === true,
    },
    isBackInStock: linkedInventory.isBackInStock,
    id: linkedInventory.id,
    productId: linkedInventory.id,
  };
}

/**
 * Resolve the inventory entity to evaluate for a cover swatch / Freeform CTA.
 *
 * Priority:
 * 1. Optional live FG product (warehouse-scoped getProducts result)
 * 2. SFRA `linkedInventory` snapshot on the variant entry
 * 3. Legacy: the variant itself
 *
 * @param {Object|null|undefined} variantEntry - c_allVariantsData row
 * @param {Object|null|undefined} [liveFgProduct] - getProducts result for inventoryProductId
 * @returns {{
 *   mode: 'linked'|'unlinked'|'legacy',
 *   subject: Object|null,
 *   inventoryProductId: string|null,
 *   isBackInStock: boolean|null,
 * }}
 */
export function resolveCoverInventorySubject(variantEntry, liveFgProduct = null) {
  const inventoryProductId = getInventoryProductIdFromVariant(variantEntry);

  if (liveFgProduct) {
    return {
      mode: 'linked',
      subject: liveFgProduct,
      inventoryProductId: liveFgProduct.id || inventoryProductId,
      isBackInStock: resolveIsBackInStock(liveFgProduct, variantEntry),
    };
  }

  if (variantEntry?.linkedInventory && inventoryProductId) {
    return {
      mode: 'linked',
      subject: linkedInventoryAsVariantShape(variantEntry.linkedInventory),
      inventoryProductId,
      isBackInStock: resolveIsBackInStock(variantEntry.linkedInventory, variantEntry),
    };
  }

  if (inventoryProductId) {
    return {
      mode: 'linked',
      subject: null,
      inventoryProductId,
      isBackInStock: resolveIsBackInStock(null, variantEntry),
    };
  }

  return {
    mode: 'legacy',
    subject: variantEntry,
    inventoryProductId: null,
    isBackInStock: null,
  };
}

/**
 * Explicit `true` only enables Notify Me / back-in-stock.
 * Missing, null, or false → treat as false (silent MTO on master / hard block on FG PDP).
 *
 * @param {Object|null|undefined} source
 * @param {Object|null|undefined} variantEntry
 * @returns {boolean}
 */
function resolveIsBackInStock(source, variantEntry) {
  if (source && typeof source.c_isBackInStock === 'boolean') {
    return source.c_isBackInStock;
  }

  if (source && typeof source.isBackInStock === 'boolean') {
    return source.isBackInStock;
  }

  if (variantEntry?.linkedInventory && typeof variantEntry.linkedInventory.isBackInStock === 'boolean') {
    return variantEntry.linkedInventory.isBackInStock;
  }

  return false;
}

/**
 * Cover inventory decision for swatch labels / CTA / ATC routing.
 * Family A only: selected cover’s `inventoryProductId` drives stock + ATC.
 * Family B / other: legacy cover ATS (MTO) — leftover links are ignored.
 *
 * @param {Object|null|undefined} product - Master
 * @param {Object|null|undefined} variantEntry - Selected cover from c_allVariantsData
 * @param {string|null|undefined} [inventoryIdsFromCookie]
 * @param {Object|null|undefined} [liveFgProduct]
 * @returns {{
 *   swatchLevel: 'in-stock'|'low-stock'|'oos'|'perpetual'|null,
 *   status: string,
 *   canAddToCart: boolean,
 *   showNotifyMe: boolean,
 *   silentMto: boolean,
 *   addFinishedGoodId: string|null,
 *   inventoryProductId: string|null,
 *   isBackInStock: boolean|null,
 *   mode: string,
 * }}
 */
export function getCoverInventoryDecision(
  product,
  variantEntry,
  inventoryIdsFromCookie,
  liveFgProduct = null
) {
  const usesFamilyACoverModel = isInventoryFamilyA(product);
  const resolved = resolveCoverInventorySubject(variantEntry, liveFgProduct);

  if (usesFamilyACoverModel && !resolved.inventoryProductId) {
    return {
      swatchLevel: 'oos',
      status: INVENTORY_STATUS.OUT_OF_STOCK,
      canAddToCart: false,
      showNotifyMe: !INVENTORY_POLICY.UNLINKED_VARIANT_DISABLE_ATC,
      silentMto: false,
      addFinishedGoodId: null,
      inventoryProductId: null,
      isBackInStock: null,
      mode: 'unlinked',
    };
  }

  if (!usesFamilyACoverModel || resolved.mode === 'legacy') {
    const inv = getInventory(product, variantEntry, inventoryIdsFromCookie);

    return {
      swatchLevel: getStockLevelFromAtsFields(variantEntry),
      status: inv.status,
      canAddToCart: inv.canAddToCart,
      showNotifyMe: inv.showNotifyMe,
      silentMto: false,
      addFinishedGoodId: null,
      inventoryProductId: null,
      isBackInStock: null,
      mode: 'legacy',
    };
  }

  if (!resolved.subject) {
    return {
      swatchLevel: 'oos',
      status: INVENTORY_STATUS.UNKNOWN,
      canAddToCart: false,
      showNotifyMe: false,
      silentMto: false,
      addFinishedGoodId: null,
      inventoryProductId: resolved.inventoryProductId,
      isBackInStock: resolved.isBackInStock,
      mode: 'linked-pending',
    };
  }

  const inv = getFinishedGoodInventory(resolved.subject, inventoryIdsFromCookie);
  // Swatch from tracked FG only — perpetual / sentinel FG is forced to OOS (never price-rule).
  const atsFields = {
    ats: inv.ats ?? 0,
    orderable: inv.canAddToCart === true,
    perpetual: false,
  };
  const swatchLevel = getStockLevelFromAtsFields(atsFields);

  const isInStock =
    inv.canAddToCart &&
    typeof inv.ats === 'number' &&
    inv.ats > 0 &&
    (inv.status === INVENTORY_STATUS.IN_STOCK || inv.status === INVENTORY_STATUS.LOW_STOCK);

  if (isInStock) {
    return {
      swatchLevel,
      status: inv.status,
      canAddToCart: true,
      showNotifyMe: false,
      silentMto: false,
      addFinishedGoodId: INVENTORY_POLICY.ATC_ADD_FINISHED_GOOD_WHEN_IN_STOCK ?
        resolved.inventoryProductId :
        null,
      inventoryProductId: resolved.inventoryProductId,
      isBackInStock: resolved.isBackInStock,
      mode: 'linked-in-stock',
    };
  }

  const isBackInStock = resolved.isBackInStock === true;

  if (isBackInStock) {
    return {
      swatchLevel: 'oos',
      status: INVENTORY_STATUS.OUT_OF_STOCK,
      canAddToCart: false,
      showNotifyMe: true,
      silentMto: false,
      addFinishedGoodId: null,
      inventoryProductId: resolved.inventoryProductId,
      isBackInStock: true,
      mode: 'linked-oos-notify',
    };
  }

  // OOS + isBackInStock false/undefined on master → silent MTO (no OOS badge).
  return {
    swatchLevel: null,
    status: INVENTORY_STATUS.IN_STOCK,
    canAddToCart: true,
    showNotifyMe: false,
    silentMto: true,
    addFinishedGoodId: null,
    inventoryProductId: resolved.inventoryProductId,
    isBackInStock: false,
    mode: 'linked-oos-silent-mto',
  };
}

/**
 * When Family A linked FG is in stock, ATC must be a single line = FG id
 * (not cover variant + option children). Shared by in-page Complex ATC and
 * floating AddToCartStrip.
 *
 * @param {Object} args
 * @param {Object|null|undefined} args.product - Master product
 * @param {Object|null|undefined} args.selections - committedSelection.selections
 * @param {Object|null} args.primaryItem - From buildPrimaryCartItem
 * @param {Array} [args.childItems] - From buildChildCartItems
 * @param {string|null|undefined} [args.inventoryIdsFromCookie]
 * @param {Object|null|undefined} [args.liveFgProduct] - Hydrated FG (price + inventory)
 * @returns {{primaryItem: Object|null, childItems: Array}}
 */
export function applyFinishedGoodAtcOverride({
  product,
  selections,
  primaryItem,
  childItems = [],
  inventoryIdsFromCookie = null,
  liveFgProduct = null,
}) {
  if (!primaryItem) {
    return {primaryItem: null, childItems: []};
  }

  if (!isInventoryFamilyA(product)) {
    return {primaryItem, childItems};
  }

  const coverId = selections?.cover?.productId ?? selections?.cover?.variantId;
  const coverEntry = coverId ? getSelectedVariantProduct(product, coverId) : null;
  const decision = getCoverInventoryDecision(
    product,
    coverEntry,
    inventoryIdsFromCookie,
    liveFgProduct
  );

  if (decision.addFinishedGoodId) {
    const fgPrice =
      liveFgProduct?.price ??
      liveFgProduct?.c_priceProcessed?.salePrice ??
      (primaryItem.price > 0 ? primaryItem.price : null);

    return {
      primaryItem: {
        productId: decision.addFinishedGoodId,
        quantity: 1,
        ...(fgPrice > 0 ? {price: fgPrice} : {}),
      },
      childItems: [],
    };
  }

  return {primaryItem, childItems};
}

/**
 * Finished-good PDP inventory / CTA decision (dedicated FG URL).
 *
 * @param {Object|null|undefined} product - FG product
 * @param {string|null|undefined} [inventoryIdsFromCookie]
 * @returns {{
 *   status: string,
 *   canAddToCart: boolean,
 *   showNotifyMe: boolean,
 * }}
 */
export function getFinishedGoodInventoryDecision(product, inventoryIdsFromCookie) {
  const inv = getFinishedGoodInventory(product, inventoryIdsFromCookie);
  // Explicit true only → Notify Me; missing/false → not purchasable on FG PDP.
  const isBackInStock = product?.c_isBackInStock === true;

  if (
    inv.canAddToCart &&
    typeof inv.ats === 'number' &&
    inv.ats > 0
  ) {
    return {
      status: inv.status,
      canAddToCart: true,
      showNotifyMe: false,
    };
  }

  if (isBackInStock) {
    return {
      status: INVENTORY_STATUS.OUT_OF_STOCK,
      canAddToCart: false,
      showNotifyMe: true,
    };
  }

  return {
    status: INVENTORY_STATUS.OUT_OF_STOCK,
    canAddToCart: false,
    showNotifyMe: false,
  };
}

/**
 * Finished good with empty/absent selectedOptions + longDescription (simple FG PDP body).
 *
 * @param {Object|null|undefined} product
 * @returns {boolean}
 */
export function isFinishedGoodSimple(product) {
  if (!product) return false;

  const selectedOptions = product.c_selectedOptions;
  const hasSelectedOptions = Array.isArray(selectedOptions) && selectedOptions.length > 0;

  if (hasSelectedOptions) return false;

  const longDescription = product.longDescription || product.c_longDescription;
  const hasLongDescription =
    (typeof longDescription === 'string' && longDescription.trim().length > 0) ||
    (longDescription && typeof longDescription === 'object' && longDescription.source);

  const hasPackageMaster = Boolean(product.c_packageMasterId);
  const hasCoverVariant = Boolean(product.c_coverVariantId);

  return Boolean(hasLongDescription && (hasPackageMaster || hasCoverVariant));
}
